// WebAudio procedural SFX engine with master/SFX volume and voice management.
// No downloaded samples.

import { EventBus, GameEvent } from '../sim/events.js';
import type { Settings, WeaponDef } from '../types.js';
import { CUE_FACTORIES, type CueId } from './synth.js';
import { MusicEngine } from './music-engine.js';
import weaponsData from '../content/weapons.json';

const VOICE_CAP = 32;

interface Voice {
  source: AudioBufferSourceNode;
  gain: GainNode;
  startTime: number;
  priority: number;
}

const CUE_PITCH_VARIATION: Record<CueId, number> = {
  'audio.weapons.lance-fire': 0.05,
  'audio.weapons.lance-hit': 0.07,
  'audio.weapons.laser-fire': 0.04,
  'audio.weapons.laser-hit': 0.06,
  'audio.enemy.death-standard': 0.08,
  'audio.player.hit': 0,
  'audio.player.dash': 0.05,
  'audio.pickup.xp': 0.06,
  'audio.ui.click': 0,
  'audio.ui.confirm': 0,
  'audio.ui.back': 0,
  'audio.wave.start': 0,
  'audio.wave.clear': 0,
  'audio.wave.boss': 0,
  'audio.music.start': 0
};

const CUE_PRIORITY: Record<CueId, number> = {
  'audio.weapons.lance-fire': 2,
  'audio.weapons.lance-hit': 2,
  'audio.weapons.laser-fire': 3,
  'audio.weapons.laser-hit': 2,
  'audio.enemy.death-standard': 2,
  'audio.player.hit': 4,
  'audio.player.dash': 2,
  'audio.pickup.xp': 2,
  'audio.ui.click': 2,
  'audio.ui.confirm': 2,
  'audio.ui.back': 2,
  'audio.wave.start': 3,
  'audio.wave.clear': 3,
  'audio.wave.boss': 4,
  'audio.music.start': 2
};

// Minimum seconds between plays of the same cue; 0 = never coalesced.
// High-rate fire/hit/xp cues are coalesced so they cannot flood the voice cap.
const CUE_MIN_INTERVAL: Record<CueId, number> = {
  'audio.weapons.lance-fire': 0.05,
  'audio.weapons.lance-hit': 0.05,
  'audio.weapons.laser-fire': 0.05,
  'audio.weapons.laser-hit': 0.05,
  'audio.enemy.death-standard': 0,
  'audio.player.hit': 0,
  'audio.player.dash': 0,
  'audio.pickup.xp': 0.05,
  'audio.ui.click': 0,
  'audio.ui.confirm': 0,
  'audio.ui.back': 0,
  'audio.wave.start': 0,
  'audio.wave.clear': 0,
  'audio.wave.boss': 0,
  'audio.music.start': 0
};

function isCueId(id: string): id is CueId {
  return id in CUE_FACTORIES;
}

// Per-weapon fire/hit cue lookup built from content, so projectile spawn and
// hit events route to the firing weapon's own cues. Weapons whose cues are
// missing or unregistered fall back to the lance cues at the call site.
const WEAPON_AUDIO_CUES = new Map<string, { fire: CueId; hit: CueId }>();
for (const def of (weaponsData as { entries: WeaponDef[] }).entries) {
  const { fire_cue, hit_cue } = def.audio;
  if (isCueId(fire_cue) && isCueId(hit_cue)) {
    WEAPON_AUDIO_CUES.set(def.id, { fire: fire_cue, hit: hit_cue });
  }
}

function weaponFireCue(weaponId: unknown): CueId {
  return WEAPON_AUDIO_CUES.get(String(weaponId))?.fire ?? 'audio.weapons.lance-fire';
}

function weaponHitCue(weaponId: unknown): CueId {
  return WEAPON_AUDIO_CUES.get(String(weaponId))?.hit ?? 'audio.weapons.lance-hit';
}

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private compressor: DynamicsCompressorNode | null = null;
  private readonly buffers = new Map<CueId, AudioBuffer>();
  private readonly voices: Voice[] = [];
  private readonly lastPlayAt = new Map<CueId, number>();
  private readonly music = new MusicEngine();
  private masterVolume = 0.8;
  private sfxVolume = 0.8;
  private musicVolume = 0.7;
  private ready = false;

  constructor(settings: Settings) {
    this.masterVolume = settings.audio.master / 100;
    this.sfxVolume = settings.audio.sfx / 100;
    this.musicVolume = settings.audio.music / 100;
  }

  async init(): Promise<void> {
    if (this.ready) return;
    const AC = globalThis.AudioContext || (globalThis as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) {
      // Headless / test environment: remain silent but marked ready.
      this.ready = true;
      return;
    }
    this.ctx = new AC();
    this.masterGain = this.ctx.createGain();
    this.sfxGain = this.ctx.createGain();
    this.compressor = this.ctx.createDynamicsCompressor();
    this.compressor.threshold.value = -9;
    this.compressor.knee.value = 0;
    this.compressor.ratio.value = 20;
    this.compressor.attack.value = 0.001;
    this.compressor.release.value = 0.1;
    this.sfxGain.connect(this.compressor);
    this.musicGain = this.ctx.createGain();
    this.musicGain.connect(this.compressor);
    this.compressor.connect(this.masterGain);
    this.masterGain.connect(this.ctx.destination);
    this.music.init(this.ctx, this.musicGain);
    this.music.setVolume(this.musicVolume);
    this.applyVolumes();

    const promises: Promise<void>[] = [];
    for (const [id, factory] of Object.entries(CUE_FACTORIES)) {
      promises.push(
        Promise.resolve(factory()).then((buf) => {
          this.buffers.set(id as CueId, buf);
        })
      );
    }
    await Promise.all(promises);
    this.ready = true;
  }

  resumeOnGesture(): void {
    if (this.ctx && this.ctx.state === 'suspended') {
      void this.ctx.resume();
    }
  }

  setMaster(volume: number): void {
    this.masterVolume = volume / 100;
    this.applyVolumes();
  }

  setSfx(volume: number): void {
    this.sfxVolume = volume / 100;
    this.applyVolumes();
  }

  setMusicVolume(volume: number): void {
    this.musicVolume = volume / 100;
    this.music.setVolume(this.musicVolume);
  }

  // Start the procedural streaming soundtrack for a run. Deterministic:
  // the same seed always produces the same sequence of themes. Restarts
  // cleanly if music is already playing (e.g. run restart).
  startMusic(seed: number): void {
    if (!this.ready) return;
    this.music.stop();
    this.music.setSeed(seed);
    this.music.start();
    this.playCue('audio.music.start');
  }

  stopMusic(): void {
    this.music.stop();
  }

  /** Tiny square-wave speech chirp for the captain's typewriter (25 ms). */
  playChirp(pitch: number): void {
    if (!this.ready) return;
    const ctx = this.ctx;
    if (!ctx || !this.sfxGain) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'square';
    osc.frequency.setValueAtTime(160 * pitch, ctx.currentTime);
    gain.gain.setValueAtTime(0.05, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.025);
    osc.connect(gain);
    gain.connect(this.sfxGain);
    osc.start();
    osc.stop(ctx.currentTime + 0.03);
  }

  playCue(cue: CueId): void {
    if (!this.ready) return;
    const ctx = this.ctx;
    if (!ctx || !this.sfxGain) return;
    const minInterval = CUE_MIN_INTERVAL[cue] || 0;
    if (minInterval > 0) {
      const last = this.lastPlayAt.get(cue);
      if (last !== undefined && ctx.currentTime - last < minInterval) return;
    }
    const buffer = this.buffers.get(cue);
    if (!buffer) return;

    const variation = CUE_PITCH_VARIATION[cue] || 0;
    const rate = variation > 0 ? 1 + (Math.random() * 2 - 1) * variation : 1;
    const priority = CUE_PRIORITY[cue] || 2;

    if (this.voices.length >= VOICE_CAP) {
      // Steal lowest-priority, then oldest voice.
      let stealIndex = -1;
      let stealPriority = priority;
      for (let i = 0; i < this.voices.length; i++) {
        const v = this.voices[i];
        if (v.priority < stealPriority || (v.priority === stealPriority && stealIndex === -1)) {
          stealIndex = i;
          stealPriority = v.priority;
        }
      }
      if (stealIndex >= 0) {
        const victim = this.voices[stealIndex];
        try {
          victim.source.stop();
          victim.source.disconnect();
          victim.gain.disconnect();
        } catch {
          // ignore
        }
        this.voices.splice(stealIndex, 1);
      } else {
        return;
      }
    }

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = rate;
    const gain = ctx.createGain();
    gain.gain.value = 0.5;
    source.connect(gain);
    gain.connect(this.sfxGain);
    source.start();

    const voice: Voice = { source, gain, startTime: ctx.currentTime, priority };
    source.onended = () => {
      const idx = this.voices.indexOf(voice);
      if (idx >= 0) this.voices.splice(idx, 1);
      try {
        source.disconnect();
        gain.disconnect();
      } catch {
        // ignore
      }
    };
    this.voices.push(voice);
    this.lastPlayAt.set(cue, ctx.currentTime);
  }

  bindEvents(bus: EventBus): void {
    bus.register((e: GameEvent) => {
      switch (e.type) {
        case 'spawn': {
          if (e.payload.kind === 'projectile') {
            this.playCue(weaponFireCue(e.payload.weapon_id));
          }
          break;
        }
        case 'hit': {
          this.playCue(weaponHitCue(e.payload.weapon_id));
          break;
        }
        case 'death': {
          this.playCue('audio.enemy.death-standard');
          break;
        }
        case 'player_hit': {
          this.playCue('audio.player.hit');
          break;
        }
        case 'dash': {
          if (e.payload.phase === 'start') {
            this.playCue('audio.player.dash');
          }
          break;
        }
        case 'pickup': {
          if (e.payload.kind === 'xp_mote') {
            this.playCue('audio.pickup.xp');
          }
          break;
        }
        case 'wave_start': {
          this.playCue(e.payload.boss ? 'audio.wave.boss' : 'audio.wave.start');
          break;
        }
        case 'wave_clear': {
          this.playCue('audio.wave.clear');
          break;
        }
      }
    });
  }

  private applyVolumes(): void {
    if (this.masterGain) {
      this.masterGain.gain.setTargetAtTime(this.masterVolume, this.ctx?.currentTime || 0, 0.02);
    }
    if (this.sfxGain) {
      this.sfxGain.gain.setTargetAtTime(this.sfxVolume, this.ctx?.currentTime || 0, 0.02);
    }
  }
}
